import { ReactNode } from 'react';
import { Icon as MUIIcon, SvgIcon, SxProps } from '@mui/material';

import colors from '@atom/styles/colors';

import { IconComponent } from '../icons';

export type IconSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

const getSizeValue = (size: IconSize) => {
  switch (size) {
    case 'sm':
      return '1rem';
    case 'lg':
      return '2rem';
    case 'xl':
      return '3rem';
    case 'xxl':
      return '3.5rem';
    case 'md':
    default:
      return '1.5rem';
  }
};

type CustomProps = {
  /**
   * SVG icon component from `@atom/mui/icons`
   */
  icon?: IconComponent;
  /**
   * Size token. Defaults to `md` (1.5rem / 24px).
   */
  size?: IconSize;
  /**
   * Color applied to the icon. Defaults to neutral gray.
   */
  color?: string;
  /**
   * Custom styles
   */
  sx?: SxProps;
  /**
   * Inline styles applied to the underlying icon element.
   */
  style?: any;
  /**
   * Generate an invisible icon (occupies space, no glyph).
   */
  invisible?: boolean;
};

type AtomMUIProps = {
  children?: ReactNode;
  className?: string;
  onClick?: (event: any) => void;
  'data-cy'?: string;
};

export type IconProps = CustomProps & AtomMUIProps;

export const Icon = (props: IconProps) => {
  const { icon, size = 'md', color, invisible, sx, children, ...rest } = props;

  const baseSx: SxProps = {
    color: color || colors.neutral.gray,
    fontSize: getSizeValue(size),
    height: 'auto',
    paddingLeft: '0',
    visibility: invisible ? 'hidden' : 'inherit',
  };

  const mergedSx: SxProps = sx ? [baseSx, sx] : baseSx;

  if (icon) {
    return <SvgIcon component={icon} {...rest} sx={mergedSx} />;
  }

  // Container mode: the font-ligature migration is complete, but `<Icon>` is
  // still used to wrap arbitrary nodes (an `<img>` of a custom SVG asset, a
  // priority badge, etc.) in a consistent icon-sized box.
  return (
    <MUIIcon {...rest} sx={mergedSx}>
      {children}
    </MUIIcon>
  );
};

export default Icon;
